"use client";

import React, { useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ScanSearch, FileCode2, TrendingUp, Target, Presentation, ArrowRight, CircleCheck as CheckCircle2, Clock, ShieldCheck, Sparkles, ChartBar as BarChart2, ListOrdered } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const auditPhases = [{
  step: "01",
  title: "Discovery & Website Review",
  timeline: "Scope Dependent",
  icon: ScanSearch,
  summary: "Understanding your business goals, organic search history, and technical architecture.",
  description: "I begin by reviewing your commercial objectives, target audience, and current organic footprint. We examine historical search data, past migrations, CMS nuances, and strategic priorities to ensure the audit focuses on the areas with the highest business impact.",
  deliverables: ["Initial business goals & target market alignment", "Historical search performance & migration review", "Scope definition & critical URL template mapping"],
  personalNote: "Starting with your commercial objectives ensures the audit addresses genuine commercial priorities rather than isolated technical trivia."
}, {
  step: "02",
  title: "Data Collection & Crawling",
  timeline: "Scope Dependent",
  icon: FileCode2,
  summary: "Comprehensive multi-crawler diagnostic and data gathering across your website.",
  description: "I execute enterprise-level crawls across your site using tools like Screaming Frog, Sitebulb, and Ahrefs, alongside Google Search Console and analytics data. This captures a complete dataset on status codes, indexability, JavaScript rendering, and technical architecture.",
  deliverables: ["Full site crawl diagnostics & URL error logs", "Search Console & analytics data extraction", "Core Web Vitals & page speed performance logs"],
  personalNote: "These tools provide the data; I interpret the findings in the context of your website, search market and business priorities."
}, {
  step: "03",
  title: "Technical, Content & Authority Analysis",
  timeline: "Scope Dependent",
  icon: TrendingUp,
  summary: "In-depth evaluation of site infrastructure, content quality, search intent, and backlink authority.",
  description: "I evaluate the crawl diagnostics alongside hands-on inspection of template code, internal linking logic, schema markup, content quality, keyword cannibalisation, and backlink profiles against key search competitors to identify all ranking bottlenecks.",
  deliverables: ["Technical infrastructure, schema & indexation review", "Content quality, intent & keyword gap analysis", "Backlink profile & competitor benchmark assessment"],
  personalNote: "I examine how search engine bots render and understand your pages, cross-referencing your content and link authority against top-performing competitors."
}, {
  step: "04",
  title: "Prioritisation & Recommendations",
  timeline: "Scope Dependent",
  icon: Target,
  summary: "Structuring every finding into an impact-versus-effort matrix with developer-ready specifications.",
  description: "Not all SEO issues carry equal commercial weight. I categorize every finding into high, medium, and low priority based on potential impact and implementation effort, turning complex diagnostic findings into a structured, step-by-step roadmap.",
  deliverables: ["Impact vs. Effort prioritization matrix", "High-impact quick wins checklist", "Developer-ready technical task specifications"],
  personalNote: "I prevent developer fatigue by ensuring your engineering team works first on the exact fixes that yield the greatest search visibility gains."
}, {
  step: "05",
  title: "Findings & Action Plan",
  timeline: "Scope Dependent",
  icon: Presentation,
  summary: "Clear executive reporting and a live video walkthrough to guide your team through implementation.",
  description: "You receive a clear, bespoke audit report and action plan written specifically for your business, accompanied by a 60-minute video presentation where I explain every recommendation and answer all technical and strategic questions directly.",
  deliverables: ["Plain-English executive summary & tactical roadmap", "60-minute live video consultation & recording", "Follow-up guidance for technical implementation"],
  personalNote: "I translate complex diagnostic findings into a clear, prioritized roadmap and walk through each solution with your team so every recommendation turns into direct execution."
}];
const highlights = [{
  label: "Audit Timeline",
  value: "Scope Dependent",
  icon: Clock
}, {
  label: "Checks Executed",
  value: "Comprehensive Technical Review",
  icon: BarChart2
}, {
  label: "Delivery Standard",
  value: "Personally Reviewed",
  icon: ShieldCheck
}, {
  label: "Actionability",
  value: "Dev-Ready Tasks",
  icon: ListOrdered
}];
export default function Methodology() {
  const [activeStep, setActiveStep] = useState(0);
  const shouldReduceMotion = useReducedMotion();
  const ActiveIcon = auditPhases[activeStep].icon;
  return <section id="auditmethodology" data-nav="light" className="bg-background text-foreground relative py-16 md:py-24 overflow-hidden">
      <div className="absolute inset-0 pointer-events-none opacity-40">
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 size-96 rounded-full bg-primary/10 blur-3xl" />
        <div className="absolute bottom-10 right-10 size-80 rounded-full bg-accent/10 blur-3xl" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 relative z-10 max-w-6xl">
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <Badge variant="secondary" className="mb-4 px-3 py-1 gap-1.5 text-xs uppercase tracking-wider font-semibold">
            <Sparkles className="size-3.5 text-primary" />
            A Structured, Expert-Led Process
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-foreground font-default">
            How I Conduct Your SEO Audit
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground font-default leading-relaxed">
            Every audit is a structured, custom examination designed to turn diagnostic data into measurable organic growth. Audit duration depends on factors such as website size, number of URLs, platform and complexity, and a timeline is agreed after reviewing the website.
          </p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
          {highlights.map((item, i) => {
          const ItemIcon = item.icon;
          return <div key={item.label} data-index={i} className="bg-card border border-border rounded-xl p-4 flex items-center gap-3 shadow-xs">
                <div className="p-2.5 rounded-lg bg-primary/10 text-primary shrink-0">
                  <ItemIcon className="size-5" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground font-medium">{item.label}</p>
                  <p className="text-sm font-semibold text-foreground">{item.value}</p>
                </div>
              </div>;
        })}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-5 space-y-3">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-1 mb-2">
              Audit Phases
            </p>
            {auditPhases.map((phase, i) => {
            const PhaseIcon = phase.icon;
            const isActive = activeStep === i;
            return <Card key={phase.step} data-index={i} className={`cursor-pointer transition-all duration-200 border ${isActive ? "border-primary bg-card shadow-md" : "border-border bg-card/60 hover:border-primary/50 hover:bg-card"}`} onClick={() => setActiveStep(i)}>
                  <CardContent className="p-4 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3.5">
                      <div className={`size-10 rounded-lg flex items-center justify-center font-bold text-sm shrink-0 transition-colors ${isActive ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>
                        {phase.step}
                      </div>
                      <div>
                        <h3 className="font-semibold text-sm text-foreground leading-snug">
                          {phase.title}
                        </h3>
                        <p className="text-xs text-muted-foreground flex items-center gap-1.5 mt-0.5">
                          <Clock className="size-3" />
                          {phase.timeline}
                        </p>
                      </div>
                    </div>
                    <Button size="icon" variant={isActive ? "default" : "ghost"} className="size-8 shrink-0 rounded-full" onClick={e => {
                  e.stopPropagation();
                  setActiveStep(i);
                }} aria-label={`View details for ${phase.title}`}>
                      <PhaseIcon className="size-4" />
                    </Button>
                  </CardContent>
                </Card>;
          })}
          </div>

          <div className="lg:col-span-7">
            <motion.div key={activeStep} initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.35,
            ease: "easeOut"
          }}>
              <Card className="border-border bg-card shadow-lg overflow-hidden">
                <CardHeader className="border-b border-border bg-muted/30 pb-6">
                  <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                    <Badge variant="outline" className="text-xs font-medium border-primary/30 text-primary">
                      Phase {auditPhases[activeStep].step} of 05
                    </Badge>
                    <span className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                      <Clock className="size-3.5 text-primary" />
                      Estimated Duration: {auditPhases[activeStep].timeline}
                    </span>
                  </div>
                  <CardTitle className="text-xl sm:text-2xl font-bold text-foreground flex items-center gap-2.5">
                    <ActiveIcon className="size-6 text-primary shrink-0" />
                    {auditPhases[activeStep].title}
                  </CardTitle>
                  <CardDescription className="text-sm text-muted-foreground mt-1">
                    {auditPhases[activeStep].summary}
                  </CardDescription>
                </CardHeader>

                <CardContent className="pt-6 space-y-6">
                  <div>
                    <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
                      Overview & Focus Areas
                    </h4>
                    <p className="text-sm text-foreground leading-relaxed">
                      {auditPhases[activeStep].description}
                    </p>
                  </div>

                  <Separator />

                  <div>
                    <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">
                      Key Phase Deliverables
                    </h4>
                    <ul className="space-y-2.5">
                      {auditPhases[activeStep].deliverables.map((item, idx) => <li key={idx} data-index={idx} className="flex items-start gap-2.5 text-sm text-foreground">
                          <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                          <span>{item}</span>
                        </li>)}
                    </ul>
                  </div>

                  <div className="bg-secondary/50 border border-secondary p-4 rounded-xl">
                    <div className="flex items-center gap-2 mb-1 text-xs font-semibold text-foreground">
                      <ShieldCheck className="size-4 text-primary shrink-0" />
                      Yassine&apos;s Approach
                    </div>
                    <p className="text-xs text-muted-foreground italic leading-relaxed">
                      &quot;{auditPhases[activeStep].personalNote}&quot;
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>

        <div className="mt-16 bg-card border border-border rounded-2xl p-6 sm:p-8 md:p-10 shadow-md flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="max-w-xl text-center md:text-left">
            <h3 className="text-xl sm:text-2xl font-semibold text-foreground">
              Ready for an actionable, hype-free SEO audit?
            </h3>
            <p className="mt-2 text-sm text-muted-foreground">
              Book a free strategy call directly with me to discuss your website goals and timeline.
            </p>
          </div>
          <div className="shrink-0">
            <Button asChild size="lg" className="rounded-md font-medium">
              <Link to="/ContactPage">
                Book a Free SEO Consultation
                <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}